import { EmblaCarouselType } from 'embla-carousel';
import { useState, useCallback, useEffect, useRef } from 'react';

const BUTTON_REMOVAL_DELAY = 1000;

type ArrowButtonState = {
    disabled: boolean;
    hidden: boolean;
    onClick: () => void;
};

type UseCarouselArrowButtonsResult = {
    prev: ArrowButtonState;
    next: ArrowButtonState;
};

/**
 * React hook for managing Embla Carousel arrow button states and handlers.
 *
 * Tracks whether the previous/next navigation buttons are disabled and
 * manages delayed hiding of buttons for fade-out animations or improved UX.
 * Provides click handlers for navigating the carousel.
 *
 * @param {EmblaCarouselType | undefined} emblaApi - Embla Carousel API instance, or undefined.
 * @returns {{
 *   prev: { disabled: boolean, hidden: boolean, onClick: () => void },
 *   next: { disabled: boolean, hidden: boolean, onClick: () => void }
 * }}
 * An object for each arrow: button disabled state, hidden state, and click handler.
 */
export function useCarouselArrowButtons(emblaApi: EmblaCarouselType | undefined): UseCarouselArrowButtonsResult {
    // State to track whether navigation buttons are disabled (can user navigate prev/next)
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);

    // State to track whether buttons should be hidden (removed from DOM after delay)
    const [hidePrev, setHidePrev] = useState(true);
    const [hideNext, setHideNext] = useState(true);

    // Refs to hold timeout IDs to clear them on cleanup or updates
    const prevTimeoutRef = useRef<number | undefined>(null);
    const nextTimeoutRef = useRef<number | undefined>(null);

    // Updates the disabled state of the buttons based on Embla's ability to scroll
    const updateButtonStates = useCallback((embla: EmblaCarouselType) => {
        setPrevDisabled(!embla.canScrollPrev());
        setNextDisabled(!embla.canScrollNext());
    }, []);

    // Handlers to scroll carousel on button clicks
    const handlePrevClick = useCallback(() => {
        emblaApi?.scrollPrev();
    }, [emblaApi]);

    const handleNextClick = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    // Set up embla event listeners to track carousel state changes and update button disabled states accordingly
    useEffect(() => {
        if (!emblaApi) {
            return undefined;
        }

        // Initial update of button states when embla instance becomes available
        updateButtonStates(emblaApi);

        // Event handlers to update disabled state on carousel events
        const onSelect = () => updateButtonStates(emblaApi);
        const onReInit = () => updateButtonStates(emblaApi);

        // Attach event listeners
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onReInit);

        // Cleanup listeners on unmount or emblaApi change
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onReInit);
        };
    }, [emblaApi, updateButtonStates]);

    // Effect that manages hiding buttons with a delay after they become disabled
    useEffect(() => {
        // Clear any existing hide timeouts when disabled state changes or effect runs again
        if (prevTimeoutRef.current) clearTimeout(prevTimeoutRef.current);
        if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);

        // If prev button is disabled, start a timeout to hide it after BUTTON_REMOVAL_DELAY ms
        // This delay allows fade-out animations or prevents accidental clicks underneath the button
        if (prevDisabled) {
            prevTimeoutRef.current = window.setTimeout(() => setHidePrev(true), BUTTON_REMOVAL_DELAY);
        } else {
            // If enabled again, immediately show the button
            setHidePrev(false);
        }

        // Same logic applied to the next button
        if (nextDisabled) {
            nextTimeoutRef.current = window.setTimeout(() => setHideNext(true), BUTTON_REMOVAL_DELAY);
        } else {
            setHideNext(false);
        }

        // Cleanup timeouts on unmount or before rerunning effect to prevent memory leaks
        return () => {
            if (prevTimeoutRef.current) clearTimeout(prevTimeoutRef.current);
            if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);
        };
    }, [prevDisabled, nextDisabled]);

    // Return objects for previous and next buttons with their disabled, hidden state and click handlers
    return {
        prev: {
            disabled: prevDisabled,
            hidden: hidePrev,
            onClick: handlePrevClick,
        },
        next: {
            disabled: nextDisabled,
            hidden: hideNext,
            onClick: handleNextClick,
        },
    };
}
